import { useRef, useState, useEffect } from 'react'
import { OverlayPortal } from './ModalPortal'
import { useNavigate } from 'react-router-dom'
import { X, Settings, Bell, Globe, FileText, Camera, LogOut, ChevronRight, Activity } from 'lucide-react'
import { useUserStore, useProfileStore } from '../stores'
import { useI18n } from '../i18n'
import { unsubscribePushOnServer } from '../services/push'
import { stopNativePush } from '../services/nativePush'
import LanguageSelector from './LanguageSelector'
import { APP_VERSION } from '../utils/appVersion'

/**
 * HamburgerMenu v156.1.3 — Redesigned slide-out menu
 * 
 * Design: Navy gradient header with profile, icon containers for menu items,
 * promoted "Find a Doctor" card, staggered slide-in animation.
 * 
 * Used on all 4 tab screens: Home, Cases, Visits, Profile.
 * 
 * @param {boolean} isOpen - Controls visibility
 * @param {function} onClose - Called when menu should close
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'

function MenuSectionLabel({ children, delay = 0 }) {
  return (
    <p
      className="text-[9px] font-bold uppercase tracking-[0.12em] mb-1 mt-5 first:mt-0 px-1"
      style={{ color: '#94A3B8', animation: `hmStagger 0.3s ease both`, animationDelay: `${delay}ms` }}
    >
      {children}
    </p>
  )
}

function MenuItem({ icon: Icon, label, subtitle, accent, danger, onClick, delay = 0 }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 py-2.5 px-1 active:bg-gray-50 rounded-lg transition-colors"
      style={{ animation: `hmStagger 0.3s ease both`, animationDelay: `${delay}ms` }}
    >
      <div
        className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center flex-shrink-0"
        style={{
          backgroundColor: danger ? '#FEF2F2' : accent ? `${BLUE}10` : '#F1F5F9'
        }}
      >
        <Icon size={16} style={{ color: danger ? '#DC2626' : accent ? BLUE : '#64748B' }} />
      </div>
      <div className="flex-1 text-left min-w-0">
        <span className={`text-[13px] font-semibold block ${danger ? 'text-red-500' : ''}`} style={danger ? {} : { color: NAVY }}>
          {label}
        </span>
        {subtitle && (
          <p className="text-[10px] mt-0.5" style={{ color: '#94A3B8' }}>{subtitle}</p>
        )}
      </div>
      {!danger && (
        <ChevronRight size={14} style={{ color: '#CBD5E1' }} className="flex-shrink-0" />
      )}
    </button>
  )
}

export default function HamburgerMenu({ isOpen, onClose }) {
  const navigate = useNavigate()
  const { t, currentLanguage } = useI18n()
  const photoInputRef = useRef(null)
  const [languageOpen, setLanguageOpen] = useState(false)

  // Store hooks
  const user = useUserStore((state) => state.user)
  const logout = useUserStore((state) => state.logout)
  const profile = useProfileStore((state) => state.profile)
  const setProfilePhoto = useProfileStore((state) => state.setProfilePhoto)

  // Derived
  const firstName = profile?.firstName || user?.firstName || ''
  const lastName = profile?.lastName || user?.lastName || ''
  const initials = `${firstName.charAt(0).toUpperCase()}${lastName?.charAt(0)?.toUpperCase() || ''}`
  const orellaId = user?.orellaMemberId || ''
  const email = user?.email || profile?.email || ''

  // Language display name. v239.13.2: currentLanguage from useI18n is the
  // SUPPORTED_LANGUAGES object, not a code — the old map lookup keyed by an
  // object always fell through to 'English'. Read nativeName directly.
  const currentLangName = currentLanguage?.nativeName || 'English'

  // Lock body scroll + hide tab bar when menu is open
  useEffect(() => {
    const tabBar = document.querySelector('.hamburger-hides-me')
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      if (tabBar) tabBar.style.display = 'none'
    } else {
      document.body.style.overflow = ''
      if (tabBar) tabBar.style.display = ''
    }
    return () => {
      document.body.style.overflow = ''
      if (tabBar) tabBar.style.display = ''
    }
  }, [isOpen])

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => { setProfilePhoto(reader.result) }
      reader.readAsDataURL(file)
    }
  }

  const handleLogout = async () => {
    // PROD-1181: an ACTIVE log out removes this browser's push endpoint at the API first (token still valid).
    try { await unsubscribePushOnServer() } catch (_) {}
    try { await stopNativePush() } catch (_) {}   // s119 (D532): this iPhone's APNs token too
    logout()
    onClose()
    navigate('/signin', { replace: true })
  }

  const go = (path) => {
    onClose()
    navigate(path, { state: { fromMenu: true } })
  }

  if (!isOpen) return <LanguageSelector isOpen={languageOpen} onClose={() => setLanguageOpen(false)} />

  return (
    <>
      <style>{`
        @keyframes hmSlideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        @keyframes hmFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes hmStagger {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <OverlayPortal>
      <div className="fixed inset-0 z-[100] flex">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/30" onClick={onClose} style={{ animation: 'hmFadeIn 0.2s ease' }} />
        
        {/* Drawer */}
        <div
          className="relative w-80 max-w-[85%] bg-white flex flex-col"
          style={{ animation: 'hmSlideIn 0.25s ease', boxShadow: '8px 0 30px rgba(0,0,0,0.08)', height: '100dvh', maxHeight: '-webkit-fill-available' }}
        >

          {/* ── Navy Header with Profile ── */}
          <div
            className="flex-shrink-0 pt-12 pb-5 px-5 relative overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #0F2438 100%)` }}
          >
            {/* Decorative circles */}
            <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ background: `${BLUE}08` }} />
            <div className="absolute -bottom-10 -left-5 w-24 h-24 rounded-full" style={{ background: `${BLUE}06` }} />

            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center z-10"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              <X size={16} style={{ color: 'rgba(255,255,255,0.5)' }} />
            </button>

            {/* Hidden file input for photo */}
            <input
              ref={photoInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoSelect}
              className="hidden"
            />

            {/* Profile row */}
            <div
              className="flex items-center gap-3.5 relative z-10"
              style={{ animation: 'hmStagger 0.3s ease both', animationDelay: '100ms' }}
            >
              <button
                onClick={() => photoInputRef.current?.click()}
                className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden relative group"
                style={{ border: `2px solid ${BLUE}50`, background: `${BLUE}15` }}
              >
                {profile?.profilePhoto ? (
                  <img src={profile.profilePhoto} alt="Profile" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white font-bold text-lg">{initials}</span>
                )}
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-active:opacity-100 transition-opacity rounded-full">
                  <Camera size={14} className="text-white" />
                </div>
              </button>
              {/* v214.2 (C-A-169): Tapping the name/email block opens Profile.
                  Avatar still opens photo picker; name/Orella ID/email is the
                  primary affordance for "go to my profile". */}
              <button
                onClick={() => go('/profile')}
                className="flex-1 min-w-0 text-left active:opacity-70 transition-opacity"
                aria-label={t('menu.openProfile') || 'Open profile'}
              >
                <p className="text-white font-bold text-[16px] truncate">{firstName} {lastName}</p>
                {orellaId && (
                  <span
                    className="inline-block text-[12px] font-bold px-2.5 py-[3px] rounded-full mt-1"
                    style={{ background: `${BLUE}25`, color: BLUE, letterSpacing: '0.02em' }}
                  >
                    {orellaId}
                  </span>
                )}
                {email && (
                  <p className="text-[12.5px] mt-1 truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>{email}</p>
                )}
              </button>
            </div>
          </div>

          {/* ── Scrollable Menu ── */}
          <div className="flex-1 overflow-y-auto px-4 pt-2 pb-4">

            {/* Find a Doctor — promoted card */}
            <button
              onClick={() => go('/doctors')}
              className="w-full mt-3 mb-1 p-3.5 rounded-2xl flex items-center gap-3 active:scale-[0.98] transition-transform text-left"
              style={{
                background: `linear-gradient(135deg, #E9F8FF 0%, white 100%)`,
                border: `1.5px solid rgba(167,217,252,0.5)`,
                animation: 'hmStagger 0.3s ease both',
                animationDelay: '150ms',
              }}
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: `${BLUE}12` }}
              >
                <Activity size={18} style={{ color: BLUE }} />
              </div>
              <div className="flex-1">
                <p className="text-[13px] font-bold" style={{ color: NAVY }}>
                  {t('doctors.doctors', 'Doctors & Clinicians')}
                </p>
                <p className="text-[10px]" style={{ color: '#94A3B8' }}>{t('doctors.findCareNearYou', 'Find care near you')}</p>
              </div>
              <ChevronRight size={14} style={{ color: BLUE }} className="flex-shrink-0" />
            </button>

            {/* General */}
            <MenuSectionLabel delay={200}>{t('menu.general') || 'General'}</MenuSectionLabel>
            <MenuItem icon={Settings} label={t('menu.settings') || 'Settings'} onClick={() => go('/settings')} delay={220} />
            <MenuItem icon={Bell} label={t('menu.notifications') || 'Notifications'} subtitle={t('menu.notificationsSub') || 'Manage alerts & reminders'} onClick={() => go('/notifications')} delay={240} />
            <MenuItem icon={Globe} label={t('menu.language') || 'Language'} subtitle={currentLangName} onClick={() => { onClose(); setLanguageOpen(true) }} delay={260} />

            {/* Legal */}
            <MenuSectionLabel delay={280}>{t('menu.legal') || 'Legal'}</MenuSectionLabel>
            <MenuItem icon={FileText} label={t('menu.privacyPolicy') || 'Privacy policy'} onClick={() => go('/settings/privacy-policy')} delay={300} />
            <MenuItem icon={FileText} label={t('menu.termsConditions') || 'Terms & conditions'} onClick={() => go('/settings/terms')} delay={320} />
          </div>

          {/* ── Sign Out Footer ── */}
          <div
            className="flex-shrink-0 px-5 border-t border-gray-100"
            style={{ animation: 'hmStagger 0.3s ease both', animationDelay: '420ms', padding: '16px 20px calc(16px + env(safe-area-inset-bottom, 16px))' }}
          >
            <button
              onClick={handleLogout}
              className="w-full py-3 rounded-xl flex items-center justify-center gap-2 text-[13px] font-semibold active:bg-red-50 transition-colors"
              style={{ color: '#DC2626', border: '1.5px solid #DC2626' }}
            >
              <LogOut size={16} />
              {t('menu.signOut') || 'Sign Out'}
            </button>
            <p className="text-center text-[10px] mt-2.5" style={{ color: '#CBD5E1' }}>
              {t('common.orellaHealth', 'Orella Health')} {APP_VERSION}
            </p>
          </div>
        </div>
      </div>
      </OverlayPortal>

      {/* Language selector portal — renders even when menu closes */}
      <LanguageSelector isOpen={languageOpen} onClose={() => setLanguageOpen(false)} />
    </>
  )
}
